import { useTranslation } from '@/i18n';
import { useVehicleLogbookStore, type VehicleLogbookEntryDto } from '@/store/useVehicleLogbookStore';
import { attentionTexts, infoTexts } from '@/pages/VehicleLogbook/logbookText';
import { formatKm } from '@/utils/distance';
import { PRIVATE } from '@/utils/vehicleTrips';

/** Business/private only matter where the tenant allows private trips (FAFD-68). */
function useShowPurposeKind(): boolean {
  return useVehicleLogbookStore((s) => s.overview?.showPurposeKind ?? false);
}

/**
 * Start and destination, purpose and — with private trips allowed — the kind of the trip
 * (official logbook, FAFD-68). An entry without any of it shows a dash.
 */
export function LogbookRouteCell({ entry }: { entry: VehicleLogbookEntryDto }) {
  const { t } = useTranslation('vehicles');
  const showKind = useShowPurposeKind();
  const route = [entry.startLocation, entry.destination].filter(Boolean).join(' → ');
  const kind = showKind ? t(`trip.kind.${entry.purposeKind === PRIVATE ? 'private' : 'business'}`) : null;
  if (!route && !entry.purpose && !kind) return <>—</>;
  return (
    <>
      {route && <div>{route}</div>}
      {entry.purpose && <div className="text-[12px] text-slate-500">{entry.purpose}</div>}
      {kind && <div className="text-[11px] text-slate-500">{kind}</div>}
    </>
  );
}

/** Issues in red, information (trip outside the working time) in amber, under the driver. */
export function LogbookIssueLines({ entry }: { entry: VehicleLogbookEntryDto }) {
  const { t } = useTranslation('vehicles');
  return (
    <>
      {attentionTexts(entry, t).map((text) => (
        <div key={text} className="mt-1 text-[11px] text-red-600">
          {text}
        </div>
      ))}
      {infoTexts(entry, t).map((text) => (
        <div key={text} className="mt-1 text-[11px] text-amber-600">
          {text}
        </div>
      ))}
    </>
  );
}

interface KmSummaryProps {
  total?: number;
  business?: number;
  private?: number;
  /** Text of the total, e.g. `logbook.vehicleTotal` ("Summe: {{km}}"). */
  totalKey: string;
}

/** "Summe: 1.234 km", with "beruflich … · privat …" where private trips are allowed. */
export function LogbookKmSummary({ total, business, private: privateKm, totalKey }: KmSummaryProps) {
  const { t } = useTranslation('vehicles');
  const showKind = useShowPurposeKind();
  return (
    <span className="whitespace-nowrap tabular-nums">
      {t(totalKey, { km: formatKm(total) })}
      {showKind && (
        <span className="ml-2 text-[12px] text-slate-500">
          {t('logbook.kindSplit', { business: formatKm(business ?? 0), private: formatKm(privateKm ?? 0) })}
        </span>
      )}
    </span>
  );
}
